/** Versioned first-party definitions. Provider metrics never enter these totals. */
export const ANALYTICS_VERSION = 1;
export const SESSION_TIMEOUT = 30 * 60_000;
export const ANALYTICS_INITIAL_SITES = [
  { id: "enkey-main", name: "Enkey Agency", origin: "https://www.enkey.eu" },
  {
    id: "enkey-shop",
    name: "Enkey Computers",
    origin: "https://shop.enkey.eu",
  },
  {
    id: "enkey-magazine",
    name: "Enkey Magazine",
    origin: "https://www.enkey.it",
  },
  { id: "sagrafica", name: "Sagrafica", origin: "https://www.sagrafica.it" },
  { id: "alexaweb", name: "Alexaweb", origin: "https://www.alexaweb.com" },
];
export type AnalyticsSite = {
  id: string;
  name: string;
  origin: string;
  enabled: boolean;
  companyId: string | null;
  projectId: string | null;
  identityEnabled: boolean;
  activatedAt: number | null;
  lastEventAt: number | null;
  archivedAt?: number | null;
};
export type AnalyticsAccess = {
  userId: string;
  superAdmin: boolean;
  permissions: string[];
  companyIds: string[];
  projectIds: string[];
};
export function siteInScope(site: AnalyticsSite, access: AnalyticsAccess) {
  if (access.superAdmin) return true;
  // Both assigned dimensions must match. No scope means no access to this module.
  return (
    Boolean(site.companyId && access.companyIds.includes(site.companyId)) &&
    (!site.projectId || access.projectIds.includes(site.projectId))
  );
}
export type Grain = "hour" | "day" | "month" | "year";
export type AnalyticsRange = {
  from: number;
  to: number;
  timezone: string;
  grain: Grain;
};
export type AnalyticsEvent = {
  siteId: string;
  id: string;
  visitor: string;
  session: string;
  at: number;
  firstSeen: number;
  returning: boolean;
  type: string;
  path: string;
  page: string;
  activeMs: number;
  scroll: number;
  referrer: string;
  campaign: string;
  country: string;
  device: string;
  browser: string;
  os: string;
  language: string;
  target: string;
  subject?: string;
  tab?: string;
};
export type MetricRow = {
  key: string;
  views: number;
  sessions: number;
  visitors: number;
  activeMs: number;
  conversions: number;
  returningVisitors?: number;
  newlyObserved?: number;
};
export function validateRange(input: Partial<AnalyticsRange>): AnalyticsRange {
  const { from, to, timezone = "Europe/Rome", grain = "day" } = input;
  if (
    !Number.isSafeInteger(from) ||
    !Number.isSafeInteger(to) ||
    from! >= to! ||
    to! - from! > 366 * 86400_000 ||
    from! < 0 ||
    !["hour", "day", "month", "year"].includes(grain)
  )
    throw new Error("INVALID_RANGE");
  try {
    new Intl.DateTimeFormat("en", { timeZone: timezone }).format();
  } catch {
    throw new Error("INVALID_TIMEZONE");
  }
  return { from: from!, to: to!, timezone, grain };
}
export function bucketKey(at: number, range: AnalyticsRange) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: range.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
    timeZoneName: "shortOffset",
  }).formatToParts(at);
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  if (range.grain === "year") return p.year;
  if (range.grain === "month") return `${p.year}-${p.month}`;
  const day = `${p.year}-${p.month}-${p.day}`;
  return range.grain === "hour" ? `${day} ${p.hour}:00 ${p.timeZoneName}` : day;
}
/** Reject sensitive routes and parameter-like path segments before persistence. */
export function analyticsPath(input: unknown): string | null {
  if (
    typeof input !== "string" ||
    !input.startsWith("/") ||
    input.startsWith("//") ||
    input.length > 1000
  )
    return null;
  let value: string;
  try {
    value = decodeURIComponent(input.split(/[?#]/)[0]);
  } catch {
    return null;
  }
  if (
    /[\s@\\]/.test(value) ||
    [...value].some((c) => c.charCodeAt(0) < 32) ||
    /(?:^|\/)(?:crm|admin|wp-admin|wp-login\.php|api|login|auth|account|my-account|reset|unsubscribe|checkout|order|orders|carrello|cart|panier|commande|mi-cuenta|mon-compte|mein-konto|il-mio-account|ordine|ordini|password)(?:\/|$)/i.test(
      value,
    )
  )
    return null;
  if (
    value
      .split("/")
      .some(
        (p) =>
          p.length > 100 ||
          /^\d{7,}$/.test(p) ||
          /^[0-9a-f]{24,}$/i.test(p) ||
          /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(p),
      )
  )
    return null;
  return value.slice(0, 500);
}
export function csvCell(value: unknown) {
  const text = String(value ?? "");
  return `"${(/^[\s]*[=+\-@\t\r]/.test(text) ? "'" + text : text).replaceAll('"', '""')}"`;
}
export function analyticsMetrics(
  all: AnalyticsEvent[],
  range: AnalyticsRange,
  now = Date.now(),
) {
  const events = all
    .filter((e) => e.at >= range.from && e.at < range.to)
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
  const visitors = new Map<string, AnalyticsEvent[]>();
  const sessions = new Map<string, AnalyticsEvent[]>();
  const visitKey = (e: AnalyticsEvent) => `${e.siteId}:${e.visitor}`;
  const sessionKey = (e: AnalyticsEvent) => `${e.siteId}:${e.session}`;
  for (const e of events) {
    const v = visitKey(e),
      s = sessionKey(e);
    const vr = visitors.get(v) ?? [];
    vr.push(e);
    visitors.set(v, vr);
    const sr = sessions.get(s) ?? [];
    sr.push(e);
    sessions.set(s, sr);
  }
  const summarize = (rows: AnalyticsEvent[]) => ({
    views: rows.filter((e) => e.type === "page_view").length,
    sessions: new Set(rows.map(sessionKey)).size,
    visitors: new Set(rows.map(visitKey)).size,
    activeMs: rows.reduce((n, e) => n + e.activeMs, 0),
    conversions: rows.filter((e) => e.type === "goal").length,
    returningVisitors: new Set(rows.filter((e) => e.returning).map(visitKey))
      .size,
    newlyObserved: new Set(
      rows.filter((e) => e.firstSeen >= range.from).map(visitKey),
    ).size,
  });
  const group = (key: (e: AnalyticsEvent) => string): MetricRow[] => {
    const groups = new Map<string, AnalyticsEvent[]>();
    for (const e of events) {
      const k = key(e) || "unknown";
      const g = groups.get(k) ?? [];
      g.push(e);
      groups.set(k, g);
    }
    return [...groups]
      .map(([key, rows]) => ({ key, ...summarize(rows) }))
      .sort(
        (a, b) =>
          b.views - a.views ||
          b.sessions - a.sessions ||
          a.key.localeCompare(b.key),
      );
  };
  let knownBefore = 0,
    returningVisitors = 0,
    repeatInPeriod = 0;
  const frequency: Record<string, number> = {
    "1": 0,
    "2": 0,
    "3–5": 0,
    "6–10": 0,
    "11+": 0,
  };
  for (const rows of visitors.values()) {
    if (rows[0].firstSeen < range.from) knownBefore++;
    if (rows.some((e) => e.returning)) returningVisitors++;
    const n = new Set(rows.map(sessionKey)).size;
    if (n > 1) repeatInPeriod++;
    frequency[
      n === 1 ? "1" : n === 2 ? "2" : n <= 5 ? "3–5" : n <= 10 ? "6–10" : "11+"
    ]++;
  }
  const paths = new Map<string, number>();
  const entry = new Map<string, number>(),
    exit = new Map<string, number>();
  let engagedSessions = 0,
    convertedSessions = 0;
  for (const rows of sessions.values()) {
    const views = rows.filter((e) => e.type === "page_view");
    if (views.length) {
      entry.set(views[0].path, (entry.get(views[0].path) ?? 0) + 1);
      exit.set(views.at(-1)!.path, (exit.get(views.at(-1)!.path) ?? 0) + 1);
    }
    // Keep separate tabs apart. Last observed page is not proof of an exit.
    const lastByTab = new Map<string, AnalyticsEvent>();
    for (const view of views) {
      const previous = lastByTab.get(view.tab ?? "default");
      lastByTab.set(view.tab ?? "default", view);
      if (!previous) continue;
      const key = `${previous.path} → ${view.path}`;
      paths.set(key, (paths.get(key) ?? 0) + 1);
    }
    const converted = rows.some((e) => e.type === "goal");
    if (converted) convertedSessions++;
    if (
      converted ||
      views.length >= 2 ||
      rows.reduce((n, e) => n + e.activeMs, 0) >= 10_000
    )
      engagedSessions++;
  }
  const totals = summarize(events);
  const measuredSessions = [...sessions.values()].filter((rows) =>
    rows.some((e) => e.type === "heartbeat"),
  ).length;
  const returnGaps: number[] = [];
  for (const rows of visitors.values()) {
    const starts = new Map<string, number>();
    for (const row of rows)
      if (!starts.has(row.session)) starts.set(row.session, row.at);
    const times = [...starts.values()].sort((a, b) => a - b);
    for (let i = 1; i < times.length; i++)
      returnGaps.push((times[i] - times[i - 1]) / 86400_000);
  }
  returnGaps.sort((a, b) => a - b);
  const ratio = (a: number, b: number) => (b ? a / b : null);
  const list = (m: Map<string, number>) =>
    [...m]
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
  const bannerNames = [
    ...new Set(
      events.filter((e) => e.type.startsWith("banner_")).map((e) => e.target),
    ),
  ];
  return {
    version: ANALYTICS_VERSION,
    ...totals,
    knownBefore,
    newlyObserved: visitors.size - knownBefore,
    returningVisitors,
    repeatInPeriod,
    returningRate: ratio(returningVisitors, visitors.size),
    sessionsPerVisitor: ratio(sessions.size, visitors.size),
    viewsPerSession: ratio(totals.views, sessions.size),
    activeSecondsPerSession: ratio(totals.activeMs / 1000, sessions.size),
    engagedSessions,
    engagementCoverage: ratio(measuredSessions, sessions.size),
    medianReturnDays: returnGaps.length
      ? (returnGaps[Math.floor((returnGaps.length - 1) / 2)] +
          returnGaps[Math.floor(returnGaps.length / 2)]) /
        2
      : null,
    bounceRate: ratio(sessions.size - engagedSessions, sessions.size),
    convertedSessions,
    conversionRate: ratio(convertedSessions, sessions.size),
    frequency,
    liveVisitors: new Set(
      events.filter((e) => e.at >= now - 300_000).map(visitKey),
    ).size,
    livePages: group((e) => e.path).filter((r) =>
      events.some((e) => e.path === r.key && e.at >= now - 300_000),
    ),
    series: group((e) => bucketKey(e.at, range)).sort((a, b) =>
      a.key.localeCompare(b.key),
    ),
    sites: group((e) => e.siteId),
    pages: group((e) => e.path),
    countries: group((e) => e.country),
    devices: group((e) => e.device),
    browsers: group((e) => e.browser),
    operatingSystems: group((e) => e.os),
    languages: group((e) => e.language),
    referrers: group((e) => e.referrer),
    campaigns: group((e) => e.campaign),
    paths: list(paths),
    entries: list(entry),
    lastPages: list(exit),
    banners: bannerNames.map((key) => {
      const rows = events.filter((e) => e.target === key);
      const impressions = rows.filter(
        (e) => e.type === "banner_impression",
      ).length;
      const visible = rows.filter((e) => e.type === "banner_visible").length;
      const clicks = rows.filter((e) => e.type === "banner_click").length;
      return {
        key,
        impressions,
        visible,
        clicks,
        visitors: new Set(rows.map(visitKey)).size,
        ctr: ratio(clicks, impressions),
      };
    }),
    scroll: [25, 50, 75, 90].map((threshold) => ({
      threshold,
      pages: new Set(
        events
          .filter((e) => e.type === "scroll" && e.scroll >= threshold)
          .map((e) => `${e.siteId}:${e.page}`),
      ).size,
    })),
  };
}
export type AnalyticsMetrics = ReturnType<typeof analyticsMetrics>;
